import React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { useAuth } from 'auth/Auth';

import { TermsAndConditionsDialog } from 'components/organisms';

import { TaskList } from 'pages/tasklist';
import { ThirdParty } from 'pages/third-party';
import { ThirdPartyEditPage } from 'pages/third-party-edit';
import { ThirdPartyEditApproval } from 'pages/third-party-edit-approval';
import { ThirdPartyCreate } from 'pages/third-party-create';
import { ThirdPartyProfile } from 'pages/third-party-profile';
import { ThirdPartyScreening } from 'pages/third-party-screening';
import InitiateDueDiligence from 'pages/initiate-due-diligence';
import { ReportHtml, ReportPdf } from 'pages/report';
import { Settings } from 'pages/settings';
import { NotFound } from 'pages/not-found';
import { Unauthorised } from 'pages/unauthorised';
import Forbidden from 'pages/forbidden/Forbidden';
import LoadingContainer from 'context/Loading';
import { Loading } from 'components/atoms';
import { useConfig } from 'config';
import { useUserSettings } from 'context/DataContext';
import { ThirdPartyEditInternalOwnersPage } from 'pages/third-party-internal-owners-edit';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import dayjs from 'dayjs';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

dayjs.tz.setDefault('UTC');

const App = () => {
  return <Pages />;
};

const Pages = () => {
  const { b2cGroup } = useConfig();
  const { tokenInfo } = useAuth();
  const { openTAC } = useUserSettings();

  if (!tokenInfo) {
    return (
      <LoadingContainer>
        <Loading />
      </LoadingContainer>
    );
  }

  if (!tokenInfo?.groups?.includes(b2cGroup)) {
    return <Forbidden />;
  }

  if (openTAC) {
    return <TermsAndConditionsDialog />;
  }

  return (
    <Switch>
      <Route path="/tasklist" exact>
        <AuthenticatedTemplate>
          <TaskList />
        </AuthenticatedTemplate>
      </Route>
      <Route path={['/', '/third-party']} exact>
        <AuthenticatedTemplate>
          <ThirdParty />
        </AuthenticatedTemplate>
      </Route>
      <Route path="/third-party/profile/:id" exact>
        <AuthenticatedTemplate>
          <ThirdPartyProfile />
        </AuthenticatedTemplate>
      </Route>
      <Route path="/third-party/create" exact>
        <AuthenticatedTemplate>
          <ThirdPartyCreate />
        </AuthenticatedTemplate>
      </Route>
      <Route path="/third-party/edit/:id" exact>
        <AuthenticatedTemplate>
          <ThirdPartyEditPage />
        </AuthenticatedTemplate>
      </Route>
      <Route path="/third-party/:id/internal-owners/edit" exact>
        <AuthenticatedTemplate>
          <ThirdPartyEditInternalOwnersPage />
        </AuthenticatedTemplate>
      </Route>
      <Route path="/third-party/edit-approval/:id" exact>
        <AuthenticatedTemplate>
          <ThirdPartyEditApproval />
        </AuthenticatedTemplate>
      </Route>
      <Route path="/third-party/screening/:id" exact>
        <AuthenticatedTemplate>
          <ThirdPartyScreening />
        </AuthenticatedTemplate>
      </Route>
      <Route
        path="/initiate-due-diligence/:id"
        exact
        render={(props) => {
          return (
            <AuthenticatedTemplate>
              <InitiateDueDiligence {...props} />
            </AuthenticatedTemplate>
          );
        }}
      />
      <Route path="/report/:id" exact>
        <AuthenticatedTemplate>
          <ReportHtml />
        </AuthenticatedTemplate>
      </Route>
      <Route path="/:clientId/report/:id/pdf" exact>
        <AuthenticatedTemplate>
          <ReportPdf />
        </AuthenticatedTemplate>
      </Route>
      <Route path="/settings" exact>
        <AuthenticatedTemplate>
          <Settings />
        </AuthenticatedTemplate>
      </Route>
      <Route path="/unauthorised" exact>
        <Unauthorised />
      </Route>
      <Route path="/forbidden" exact>
        <UnauthenticatedTemplate>
          <Forbidden />
        </UnauthenticatedTemplate>
      </Route>
      <Route path="/not-found" exact>
        <AuthenticatedTemplate>
          <NotFound />
        </AuthenticatedTemplate>
      </Route>
      <Route>
        <Redirect to="/" />
      </Route>
    </Switch>
  );
};

export default App;
